// src/services/todayPlan.ts
//
// Deterministic "Today" planning composition. Reuses computeWorkloadSummary for
// meeting/free-time math and FocusItem.estimatedMinutes for focus workload.
// NO time-blocking, NO task scheduling, NO AI — it only describes today.

import type { Event } from '../types/api';
import type { WorkloadSummary } from './workload';
import type { FocusItem } from './dailyExecution';

export type MeetingLoad = 'light' | 'normal' | 'heavy';
export type FocusVsCapacity = 'fits' | 'tight' | 'over' | 'unknown';

export interface DeepWorkWindow {
  start: string; // ISO
  end: string; // ISO
  minutes: number;
}

export interface TodayPlan {
  meetingCount: number;
  meetingMinutes: number;
  meetingLoad: MeetingLoad;
  freeMinutes: number;
  focusWorkloadMinutes: number;
  focusVsCapacity: FocusVsCapacity;
  deepWorkWindows: DeepWorkWindow[];
}

export interface TodayPlanOptions {
  today: string; // YYYY-MM-DD (local day)
  workdayMinutes?: number;
  workdayStartHour?: number;
  workdayEndHour?: number;
  minDeepWorkMinutes?: number;
}

const DEFAULT_EVENT_MINUTES = 60;

function classifyMeetingLoad(meetingMinutes: number, workdayMinutes: number): MeetingLoad {
  const ratio = workdayMinutes > 0 ? meetingMinutes / workdayMinutes : 0;
  if (ratio < 0.25) return 'light';
  if (ratio <= 0.6) return 'normal';
  return 'heavy';
}

/**
 * Free gaps ≥ minDeepWorkMinutes within the workday, computed from today's timed
 * events: overlaps merged, all-day events ignored, times compared in local time.
 */
export function computeDeepWorkWindows(
  events: Event[],
  opts: Required<Pick<TodayPlanOptions, 'today' | 'workdayStartHour' | 'workdayEndHour' | 'minDeepWorkMinutes'>>,
): DeepWorkWindow[] {
  const { today, workdayStartHour, workdayEndHour, minDeepWorkMinutes } = opts;
  const dayStart = new Date(`${today}T${String(workdayStartHour).padStart(2, '0')}:00:00`).getTime();
  const dayEnd = new Date(`${today}T${String(workdayEndHour).padStart(2, '0')}:00:00`).getTime();
  if (!(dayEnd > dayStart)) return [];

  // Build busy intervals from timed events overlapping the workday, clamped to it.
  const busy: Array<[number, number]> = [];
  for (const ev of events) {
    if (ev.isAllDay || !ev.startAt) continue; // ignore all-day per spec
    const s = new Date(ev.startAt).getTime();
    const e = ev.endAt ? new Date(ev.endAt).getTime() : s + DEFAULT_EVENT_MINUTES * 60_000;
    const start = Math.max(s, dayStart);
    const end = Math.min(e, dayEnd);
    if (end > start) busy.push([start, end]);
  }
  busy.sort((a, b) => a[0] - b[0]);

  // Merge overlaps.
  const merged: Array<[number, number]> = [];
  for (const [s, e] of busy) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }

  // Gaps between dayStart → merged intervals → dayEnd.
  const windows: DeepWorkWindow[] = [];
  let cursor = dayStart;
  const minMs = minDeepWorkMinutes * 60_000;
  for (const [s, e] of merged) {
    if (s - cursor >= minMs) {
      windows.push({ start: new Date(cursor).toISOString(), end: new Date(s).toISOString(), minutes: Math.round((s - cursor) / 60_000) });
    }
    cursor = Math.max(cursor, e);
  }
  if (dayEnd - cursor >= minMs) {
    windows.push({ start: new Date(cursor).toISOString(), end: new Date(dayEnd).toISOString(), minutes: Math.round((dayEnd - cursor) / 60_000) });
  }
  return windows;
}

/**
 * Compose a deterministic Today plan. `workload` is reused as-is for
 * meeting/free minutes (it already counts today's events + all-day handling).
 */
export function computeTodayPlan(
  events: Event[],
  focus: FocusItem[],
  workload: WorkloadSummary,
  opts: TodayPlanOptions,
): TodayPlan {
  const {
    today,
    workdayMinutes = 480,
    workdayStartHour = 9,
    workdayEndHour = 17,
    minDeepWorkMinutes = 90,
  } = opts;

  const meetingCount = events.filter((e) => e.startAt && e.startAt.split('T')[0] === today).length;
  const meetingMinutes = workload.scheduledMinutesToday;
  const freeMinutes = workload.freeMinutesToday;
  const focusWorkloadMinutes = focus.reduce((sum, f) => sum + (f.estimatedMinutes ?? 0), 0);

  let focusVsCapacity: FocusVsCapacity;
  if (focusWorkloadMinutes <= 0) focusVsCapacity = 'unknown';
  else if (focusWorkloadMinutes <= freeMinutes) focusVsCapacity = 'fits';
  else if (focusWorkloadMinutes <= freeMinutes * 1.25) focusVsCapacity = 'tight';
  else focusVsCapacity = 'over';

  return {
    meetingCount,
    meetingMinutes,
    meetingLoad: classifyMeetingLoad(meetingMinutes, workdayMinutes),
    freeMinutes,
    focusWorkloadMinutes,
    focusVsCapacity,
    deepWorkWindows: computeDeepWorkWindows(events, { today, workdayStartHour, workdayEndHour, minDeepWorkMinutes }),
  };
}
